import { resetUserPassword } from 'actions';
import { Alert } from 'components/ui/Alert/Alert';
import { Button } from 'components/ui/Button/Button';
import { Heading } from 'components/ui/Heading/Heading';
import { TextInput } from 'components/ui/TextInput/TextInput';
import isEmpty from 'lodash/isEmpty';
import {
  type FormEvent,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getFieldValue } from 'selectors';
import { useTranslations } from 'utils';
import { clientValidate } from 'utils/clientValidate/clientValidate';
import { errorNotifier } from 'utils/errorNotifier/errorNotifier';
import { SENDER_EMAIL_FIELD } from '../../constants';
import './UserForgotPassword.scss';

const WAIT_TIME = 75;
type UserForgotPasswordProps = {
  onLogin: () => void;
  resetUserPassword?: (email: string) => void;
  showUpdatePasswordAlert?: boolean;
} & ConnectedProps<typeof connector>;

const UserForgotPasswordComponent = ({
  onLogin,
  resetUserPassword,
  showUpdatePasswordAlert,
  senderEmail,
}: UserForgotPasswordProps) => {
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState(senderEmail);
  const [isSuccess, setIsSuccess] = useState(false);

  const i18n = useTranslations();
  const emailInputRef = useRef();

  const setFocusOnInput = () =>
    setTimeout(() => {
      emailInputRef.current?.focus();
    }, WAIT_TIME);

  // biome-ignore lint/correctness/useExhaustiveDependencies: error triggers focus, setFocusOnInput is stable
  useEffect(() => {
    setFocusOnInput();
  }, [error]);

  const validateErrors = (fields: { userEmail: string }) => {
    return (
      clientValidate.validate(fields, getConstraints(), {
        fullMessages: false,
      }) || {}
    );
  };

  const getConstraints = () => {
    return {
      userEmail: {
        email: { message: i18n.t('userForgotPassword.userEmail.invalid') },
      },
    };
  };

  const handleResetPassword = async (e: FormEvent) => {
    e.preventDefault();
    const error = validateErrors({ userEmail: userEmail });
    if (!isEmpty(error)) {
      return setError(error.userEmail[0]);
    }

    try {
      await resetUserPassword(userEmail);
    } catch (error) {
      errorNotifier.notify(error);
    } finally {
      setIsSuccess(true);
    }
  };

  const handleUserEmailChange = (_name: string, value: string) => {
    setUserEmail(value);
  };

  return (
    <>
      {isSuccess ? (
        <UserForgotPasswordSuccess onLogin={onLogin} userEmail={userEmail} />
      ) : (
        <UserForgotPasswordForm
          onLogin={onLogin}
          showUpdatePasswordAlert={showUpdatePasswordAlert}
          handleResetPassword={handleResetPassword}
          handleUserEmailChange={handleUserEmailChange}
          error={error}
          senderEmail={userEmail}
          forwardRef={emailInputRef}
        />
      )}
    </>
  );
};

type UserForgotPasswordSuccessProps = {
  onLogin: () => void;
  userEmail: string;
};

const UserForgotPasswordSuccess = ({
  onLogin,
  userEmail,
}: UserForgotPasswordSuccessProps) => {
  const i18n = useTranslations();

  return (
    <>
      <span className="UserForgotPassword-checked" />
      <Heading
        as="h4"
        size="large"
        className="textAlign-center marginBottom-xxl"
        id="Page-title"
      >
        {i18n.t('userForgotPassword.success.title')}
      </Heading>
      <div className="textAlign-center paddingLeft-md paddingRight-md">
        <p>{i18n.t('userForgotPassword.success.text', { userEmail })}</p>
      </div>
      <div className="UserForgotPassword-login">
        <button
          className="UserForgotPassword-button"
          onClick={onLogin}
          type="button"
          role="link"
        >
          {i18n.t('userForgotPassword.login.link')}
        </button>
      </div>
    </>
  );
};

type UserForgotPasswordFormProps = {
  error?: string;
  forwardRef?: RefObject<HTMLInputElement>;
  handleResetPassword: (e: FormEvent) => unknown;
  handleUserEmailChange: (name: string, value: string) => void;
  onLogin: () => void;
  showUpdatePasswordAlert?: boolean;
  senderEmail: string;
};

const UserForgotPasswordForm = ({
  handleResetPassword,
  handleUserEmailChange,
  error,
  onLogin,
  showUpdatePasswordAlert,
  forwardRef,
  senderEmail = '',
}: UserForgotPasswordFormProps) => {
  const i18n = useTranslations();
  return (
    <form onSubmit={handleResetPassword} className="flex flex-col h-full">
      <div className="flex-1">
        {showUpdatePasswordAlert && (
          <Alert type="warning">
            {i18n.t(`userLogin.alert.updatePasswordAlert`)}
          </Alert>
        )}
        <Heading
          as="h4"
          size="large"
          className="textAlign-center marginBottom-xxl"
          id="Page-title"
        >
          {i18n.t('userForgotPassword.title')}
        </Heading>
        <div className="UserLogin-required">
          {i18n.t('requiredField.label')}
        </div>
        <TextInput
          aria-required="true"
          autoFocus
          required
          error={error}
          floatingLabel
          forwardRef={forwardRef}
          label={i18n.t('userForgotPassword.userEmail.inputLabel') as string}
          name="userEmail"
          onChange={handleUserEmailChange}
          value={senderEmail}
        />
      </div>
      <div className="pt-6 pb-4">
        <Button
          type="submit"
          className="marginTop-xxl"
          tone="primary"
          size="full"
          data-testid="resetPasswordButton"
        >
          {i18n.t('userForgotPassword.resetPassword.buttonText')}
        </Button>
        <div className="UserForgotPassword-login">
          <button
            className="UserForgotPassword-button"
            onClick={onLogin}
            type="button"
            role="link"
          >
            {i18n.t('userForgotPassword.login.link')}
          </button>
        </div>
      </div>
    </form>
  );
};

const mapStateToProps = (state: RootState) => ({
  senderEmail: getFieldValue(state, SENDER_EMAIL_FIELD) as string,
});

const mapDispatchProps = {
  resetUserPassword,
};

const connector = connect(mapStateToProps, mapDispatchProps);

const UserForgotPassword = connector(UserForgotPasswordComponent);

export { UserForgotPassword };
